import React, { useState } from 'react';
import axios from 'axios';
import { contactInfo } from '../data/mock';
import { Mail, ArrowRight, Send, User, MessageSquare, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        message: formData.message
      });
      
      setSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
      
    } catch (err) {
      console.error('Contact form error:', err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.status === 422) {
        setError('Please check your input and try again.');
      } else {
        setError('Failed to send message. Please try again or email us directly.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-padding contact-section" id="contact">
      <div className="container">
        <div className="section-header" style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}>
          <p className="section-label">{contactInfo.title}</p>
          <h2 className="section-title">Ready to Accelerate Your Initiatives?</h2>
          <p className="section-description" style={{ maxWidth: '100%' }}>
            {contactInfo.subtitle}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-16 items-start max-w-5xl mx-auto">
          {/* Contact Info */}
          <div>
            <h3 className="font-mono text-2xl font-semibold mb-6">Get in Touch</h3>
            <p className="text-[var(--text-secondary)] mb-8 leading-relaxed">
              Whether you're planning a major software initiative, need process consulting, 
              or seeking RFQ support, our senior consultants are ready to help.
            </p>
            
            <div className="space-y-6">
              <a 
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-4 p-4 border border-[var(--border-subtle)] rounded-xl hover:border-[var(--accent-primary)] transition-colors group"
              >
                <div className="w-12 h-12 bg-[var(--accent-subtle)] rounded-xl flex items-center justify-center text-[var(--accent-primary)]">
                  <Mail size={20} />
                </div>
                <div>
                  <p className="text-sm text-[var(--text-muted)] mb-1">Email Us</p>
                  <p className="font-medium text-[var(--accent-primary)] group-hover:underline">
                    {contactInfo.email}
                  </p>
                </div>
              </a>
              
              <a 
                href={`mailto:${contactInfo.email}?subject=Consultation Request`}
                className="btn-primary w-full justify-center btn-hover-effect"
              >
                {contactInfo.cta}
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
          
          {/* Contact Form */}
          <div>
            <h3 className="font-mono text-2xl font-semibold mb-6">Send a Message</h3>
            
            {submitted ? (
              <div className="p-8 border border-[var(--accent-primary)] bg-[var(--accent-subtle)] rounded-xl text-center">
                <div className="w-16 h-16 bg-[var(--accent-primary)] rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Send size={24} className="text-[var(--text-primary)]" />
                </div>
                <h4 className="text-xl font-semibold mb-2">Message Sent!</h4>
                <p className="text-[var(--text-secondary)]">
                  Thank you for reaching out. We'll get back to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-4 border border-red-500/50 bg-red-500/10 rounded-lg flex items-center gap-3">
                    <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Name *</label>
                  <div className="relative">
                    <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="w-full bg-white border border-[var(--border-subtle)] p-4 pl-12 text-[var(--text-primary)] focus:border-[var(--accent-primary)] focus:outline-none transition-colors disabled:opacity-50"
                      placeholder="Your name"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Email *</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="w-full bg-white border border-[var(--border-subtle)] p-4 pl-12 text-[var(--text-primary)] focus:border-[var(--accent-primary)] focus:outline-none transition-colors disabled:opacity-50"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full bg-white border border-[var(--border-subtle)] p-4 text-[var(--text-primary)] focus:border-[var(--accent-primary)] focus:outline-none transition-colors disabled:opacity-50"
                    placeholder="Your company name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-2">Message *</label>
                  <div className="relative">
                    <MessageSquare size={18} className="absolute left-4 top-4 text-[var(--text-muted)]" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      minLength={10}
                      rows={4}
                      className="w-full bg-white border border-[var(--border-subtle)] p-4 pl-12 text-[var(--text-primary)] focus:border-[var(--accent-primary)] focus:outline-none transition-colors resize-none disabled:opacity-50"
                      placeholder="Tell us about your project or requirements..."
                    />
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn-primary w-full justify-center btn-hover-effect disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
